import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { sdk } from '@/services/sdk';
import { useFetch } from '@/hooks/useFetch';
import ApperIcon from '@/components/ApperIcon';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Busy, ConfirmDelete, DetailRow, PageHeader, Panel, StatusBadge } from '@/components/kit';
import { ConfirmAction } from '@/components/confirm';
import { Stars } from '@/components/inputs';
import { BackLink, recordGate } from '@/components/record';
import { PLAN_LABELS, VISIBILITY_STYLES } from '@/components/admin/ReviewForm';
import { REVIEW_FIELDS } from '@/shared/fields';
import { committed, idOf } from '@/shared/data';
import { formatLocalDate, DATE_FORMATS } from '@/utils/date';

export const route = { path: '/admin/reviews/:id', layout: 'owner', access: "['Admin_c','Staff_c'].includes(profile)" };

export default function AdminReviewDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [reply, setReply] = useState('');
  const [savingReply, setSavingReply] = useState(false);

  const { data: review, loading, error, run } = useFetch(
    () => sdk.table('reviews_c').select(REVIEW_FIELDS).get(id).then((r) => r.data ?? null),
    [id],
  );

  useEffect(() => {
    setReply(review?.reply_c ?? '');
  }, [review?.Id, review?.reply_c]);

  const back = <BackLink to="/admin/reviews" label="Back to Reviews" />;
  const gate = recordGate({ back, loading, record: review, error, run, what: 'Review' });
  if (gate) return gate;

  const orderId = idOf(review.order_c);
  const shown = review.hidden_c ? 'Hidden' : 'Visible';
  const replyChanged = reply.trim() !== (review.reply_c ?? '').trim();

  async function saveReply(e) {
    e.preventDefault();
    setSavingReply(true);
    try {
      const res = await sdk.table('reviews_c').update({ Id: review.Id, reply_c: reply.trim() });
      if (!committed(res, reply.trim() ? 'Reply posted' : 'Reply removed')) return;
      run();
    } finally {
      setSavingReply(false);
    }
  }

  async function toggleHidden() {
    const next = !review.hidden_c;
    const res = await sdk.table('reviews_c').update({ Id: review.Id, hidden_c: next });
    if (!committed(res, next ? 'Review hidden' : 'Review is visible again')) return false;
    run();
    return true;
  }

  async function remove() {
    const res = await sdk.table('reviews_c').remove(review.Id);
    if (!committed(res, 'Review deleted')) return false;
    navigate('/admin/reviews');
    return true;
  }

  return (
    <div className="mx-auto max-w-3xl">
      {back}
      <PageHeader
        eyebrow={`Review · ${formatLocalDate(review.CreatedOn, DATE_FORMATS.LONG)}`}
        title={review.Name}
        description={`by ${review.display_name_c || 'Anonymous viewer'}`}
        actions={
          <>
            <StatusBadge value={shown} styles={VISIBILITY_STYLES} />
            <Button variant="outline" size="sm" asChild>
              <Link to={`/admin/reviews/${review.Id}/edit`}>
                <ApperIcon name="Pencil" size={16} />
                Edit
              </Link>
            </Button>
            <ConfirmDelete title="Delete this review?" description="It disappears from the storefront and the customer's account." onConfirm={remove} />
          </>
        }
      />

      <Panel title="What they said" icon="Star" bodyClassName="space-y-3 py-4">
        <Stars value={review.rating_c} size={18} />
        <p className="whitespace-pre-wrap text-sm leading-relaxed">{review.body_c}</p>
      </Panel>

      <div className="my-4 flex flex-wrap gap-2">
        <ConfirmAction
          label={review.hidden_c ? 'Show on storefront' : 'Hide from storefront'}
          icon={review.hidden_c ? 'Eye' : 'EyeOff'}
          title={review.hidden_c ? 'Show this review again?' : 'Hide this review?'}
          description={
            review.hidden_c
              ? 'It goes back on the storefront with its rating and reply.'
              : 'It stays on file and in the customer account, but the public no longer sees it.'
          }
          busyLabel="Saving…"
          onConfirm={toggleHidden}
        />
      </div>

      <Panel title="Public reply" icon="Reply" bodyClassName="py-4">
        <form onSubmit={saveReply} className="space-y-3">
          <Textarea
            id="rd-reply"
            aria-label="Public reply"
            rows={3}
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            placeholder="Thanks for sticking with us. Glad the new server sorted the buffering…"
          />
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-xs text-muted-foreground">Shown under the review, signed Ghost Cable.</p>
            <Button type="submit" size="sm" disabled={savingReply || !replyChanged}>
              <Busy busy={savingReply} label={review.reply_c ? 'Save reply' : 'Post reply'} busyLabel="Saving…" />
            </Button>
          </div>
        </form>
      </Panel>

      <Panel title="Details" icon="Info" className="mt-4">
        <dl className="divide-y divide-border">
          <DetailRow label="Headline">{review.Name}</DetailRow>
          <DetailRow label="Shown as">{review.display_name_c}</DetailRow>
          <DetailRow label="Rating">{`${Number(review.rating_c || 0)} out of 5`}</DetailRow>
          <DetailRow label="Plan">{PLAN_LABELS[review.plan_c] ?? review.plan_c}</DetailRow>
          <DetailRow label="Order">
            {orderId ? (
              <Link to={`/admin/orders/${orderId}`} className="font-mono text-primary hover:underline">
                {review.order_c?.Name ?? 'View order'}
              </Link>
            ) : null}
          </DetailRow>
          <DetailRow label="Storefront">
            <StatusBadge value={shown} styles={VISIBILITY_STYLES} />
          </DetailRow>
          <DetailRow label="Reply">{review.reply_c ? <span className="whitespace-pre-wrap">{review.reply_c}</span> : null}</DetailRow>
          <DetailRow label="Posted">{formatLocalDate(review.CreatedOn, DATE_FORMATS.WITH_TIME)}</DetailRow>
        </dl>
      </Panel>
    </div>
  );
}
